import { Type } from 'class-transformer';
import { IsBoolean, IsNumber, IsNumberString, IsObject, IsOptional, IsString, ValidateNested } from 'class-validator';
import { GsapPluginDto } from './gsap-plugin.dto';
import { AnimationPropertiesDto } from './animation-properties.dto';
import { AnimationPluginConfigDto } from './animation-plugin-config.dto';

export const MAXIMUM_ANIMATION_DURATION = 5 * 60;

/**
 * Configuration settings for animation behavior at the package/creative unit level.
 * Controls global animation settings like playback, timeline markers, and plugin loading.
 */
export class AnimationConfigDto {
	/**
	 * Whether animations should automatically start playing when the ad loads.
	 * When true, the animation timeline begins immediately upon render.
	 * @default true
	 */
	@IsOptional()
	@IsBoolean()
	autoPlay?: boolean;

	/**
	 * Whether the animation timeline should restart after completion.
	 * When true, creates an infinite loop of the animation sequence.
	 * @default false
	 */
	@IsOptional()
	@IsBoolean()
	loop?: boolean;

	/**
	 * Controls visibility of timeline markers in the final rendered ad.
	 * When true, debug markers are hidden from end users but remain visible in preview/edit modes.
	 * Useful for development markers that shouldn't appear in production.
	 * @default false
	 */
	@IsOptional()
	@IsBoolean()
	hideMarkersForUser?: boolean;

	/**
	 * The total duration of the animation timeline in seconds (as string).
	 * Defines the complete timeline length that all animations operate within.
	 * Format: "10" for 10 seconds, "5.5" for 5.5 seconds.
	 */
	@IsOptional()
	totalDuration?: string;

	/**
	 * Timeline markers that define specific points in the animation sequence.
	 * Used for synchronization points, navigation targets, or event triggers.
	 * Markers can be referenced by animations to coordinate timing.
	 */
	@IsOptional()
	@ValidateNested({ each: true })
	@Type(() => TimelineMarkerDto)
	markers: TimelineMarkerDto[];

	/**
	 * Feature flag to enable experimental animation UI features.
	 * When true, unlocks beta animation tools and panels in the editor.
	 * Use with caution as experimental features may not be fully stable.
	 * @default false
	 */
	@IsOptional()
	enableExperimentalAnimationArea?: boolean;

	/**
	 * Master switch to disable all layer animations during creative unit conversion.
	 * When true, animations are stripped out during the build/export process.
	 * Useful for creating static versions or troubleshooting animation issues.
	 * @default false
	 */
	@IsOptional()
	@IsBoolean()
	disableAnimations?: boolean;

	/**
	 * GSAP plugin configurations for extended animation capabilities.
	 * Defines which GSAP plugins should be loaded and their settings.
	 * Plugins are dynamically loaded from CDN to optimize bundle size.
	 * Each plugin adds specific animation features (text, SVG, scroll, etc.).
	 */
	@IsOptional()
	@ValidateNested({ each: true })
	@Type(() => GsapPluginDto)
	gsapPlugins?: GsapPluginDto[];
}

/**
 * Defines a specific point on the animation timeline for synchronization and navigation.
 * Markers act as named reference points that animations can target for precise timing control.
 */
export class TimelineMarkerDto {
	/**
	 * Unique identifier for the marker.
	 * Used internally to reference and manage the marker.
	 */
	@IsOptional()
	@IsString()
	id: string;

	/**
	 * Human-readable display name for the marker.
	 * Shown in the UI timeline for easy identification.
	 * Example: "Intro Complete", "Main Message", "Call to Action"
	 */
	@IsOptional()
	@IsString()
	label: string;

	/**
	 * URL-safe identifier derived from the label.
	 * Used for programmatic references and deep linking.
	 * Example: "intro-complete", "main-message", "call-to-action"
	 */
	@IsOptional()
	@IsString()
	slug: string;

	/**
	 * The time position of the marker on the timeline (in seconds as string).
	 * Format: "2.5" for 2.5 seconds, "10" for 10 seconds.
	 * Animations can reference this marker as a position parameter.
	 */
	@IsOptional()
	time: string;
}

/**
 * Defines an individual animation for a layer within the creative unit.
 * Each animation represents a single tween or timeline operation in GSAP.
 * Multiple animations can be combined to create complex motion sequences.
 */
export class AnimationDto {
	/**
	 * Unique identifier for the animation instance.
	 * Used to track and manage individual animations in the timeline.
	 */
	@IsOptional()
	@IsString()
	id?: string;

	/**
	 * Descriptive name for the animation.
	 * Helps identify the animation's purpose in the editor UI.
	 * Example: "Fade In Logo", "Slide Text Left", "Scale CTA Button"
	 */
	@IsOptional()
	@IsString()
	name?: string;

	/**
	 * This is used to describe where the animation is placed within the configuration.
	 * Possible values are 'in', 'out', 'custom'.
	 */
	@IsOptional()
	@IsString()
	type?: 'in' | 'out' | 'custom' | string;

	/**
	 * Reference to the target element(s) to animate.
	 * Can be a layer ID, CSS selector, or array of selectors.
	 * The renderer maps this to actual DOM elements at runtime.
	 */
	@IsOptional()
	element?: string | string[] | any;

	/**
	 * Starting properties for the animation (for "from" and "fromTo" types).
	 * Can use typed AnimationPropertiesDto or generic Record for flexibility.
	 * Example: { opacity: 0, x: -100, scale: 0.5 }
	 */
	@IsOptional()
	@IsObject()
	from?: Record<string, any> | AnimationPropertiesDto;

	/**
	 * Ending properties for the animation (for "to" and "fromTo" types).
	 * Can use typed AnimationPropertiesDto or generic Record for flexibility.
	 * Example: { opacity: 1, x: 0, scale: 1, rotation: 360 }
	 */
	@IsOptional()
	@IsObject()
	to?: Record<string, any> | AnimationPropertiesDto;

	/**
	 * The animation method/type to be executed.
	 * "to" = animate TO these values, "from" = animate FROM these values,
	 * "fromTo" = animate from specific values to specific values, "set" = immediately set values
	 */
	@IsOptional()
	@IsString()
	method?: 'to' | 'from' | 'fromTo' | 'set' | string;

	/**
	 * Timeline position where this animation should start.
	 * Can be absolute time (number in seconds), relative position (string like "+=1" or "-=0.5"),
	 * or marker reference (string like "markerName" or "markerName+=0.5").
	 * Controls sequencing and synchronization of multiple animations.
	 */
	@IsOptional()
	position?: string | number;

	/**
	 * Duration of the animation in seconds.
	 * How long the animation takes to complete from start to end.
	 * @default 1
	 */
	@IsOptional()
	@IsNumber()
	duration?: number;

	/**
	 * Delay before the animation starts in seconds.
	 * Time to wait before beginning the animation after its position is reached.
	 * @default 0
	 */
	@IsOptional()
	@IsNumber()
	delay?: number;

	/**
	 * Easing function for the animation.
	 * Controls the rate of change during the animation.
	 * Examples: "power2.inOut", "elastic.out", "back.inOut", "linear"
	 * See GSAP_EASES constant for full list of available eases.
	 */
	@IsOptional()
	@IsString()
	ease?: string;

	/**
	 * Number of times the animation should repeat.
	 * Use -1 for infinite repeats.
	 * @default 0 (no repeat)
	 */
	@IsOptional()
	@IsNumber()
	repeat?: number;

	/**
	 * Delay between animation repeats in seconds.
	 * Only applies when repeat > 0.
	 * @default 0
	 */
	@IsOptional()
	@IsNumber()
	repeatDelay?: number;

	/**
	 * Whether the animation should play backwards on alternate repeats.
	 * Creates a back-and-forth effect.
	 * @default false
	 */
	@IsOptional()
	@IsBoolean()
	yoyo?: boolean;

	/**
	 * Easing function specifically for the yoyo (backwards) portion.
	 * If not specified, uses the same ease as forward animation.
	 */
	@IsOptional()
	@IsString()
	yoyoEase?: string;

	/**
	 * Stagger time for multiple targets in seconds.
	 * Creates a sequential delay between each target's animation start.
	 * Useful for animating lists or grids of elements.
	 */
	@IsOptional()
	@IsNumber()
	stagger?: number;

	/**
	 * Plugin-specific animation configurations.
	 * Contains settings for TextPlugin, DrawSVG, MorphSVG, MotionPath, etc.
	 * Only used when corresponding plugins are loaded.
	 */
	@IsOptional()
	@ValidateNested()
	@Type(() => AnimationPluginConfigDto)
	pluginConfig?: AnimationPluginConfigDto;

	/**
	 * Whether to pause the animation immediately upon creation.
	 * Useful for animations that should be triggered later.
	 * @default false
	 */
	@IsOptional()
	@IsBoolean()
	paused?: boolean;

	/**
	 * Whether to play the animation in reverse.
	 * @default false
	 */
	@IsOptional()
	@IsBoolean()
	reversed?: boolean;

	/**
	 * How to handle conflicting animations on the same properties.
	 * true = kill all conflicting tweens, "auto" = smart conflict resolution, false = allow conflicts
	 * @default false
	 */
	@IsOptional()
	overwrite?: boolean | 'auto';

	/**
	 * Whether to render immediately upon creation.
	 * @default false for "to", true for "from" and "fromTo"
	 */
	@IsOptional()
	@IsBoolean()
	immediateRender?: boolean;

	/**
	 * Starting values to set before animation begins.
	 * Useful for ensuring consistent starting state.
	 */
	@IsOptional()
	@IsObject()
	startAt?: Record<string, any> | AnimationPropertiesDto;

	/**
	 * Array of keyframe objects for complex multi-step animations.
	 * Each keyframe can have its own properties and timing.
	 */
	@IsOptional()
	keyframes?: Array<Record<string, any>>;
}
